import { getProgressBar } from '../../progress-bar/executeSteps'
import { getFieldMap } from './getFieldMap'
import { truncateCollectionName } from '../truncateCollectionName'
import { findController } from '../utils/findController'
import { addPage } from '../add-collection-and-page-with-repeater/addPage'

import {
  runTransactionWithRetry,
  UNDO_REDO_LABELS,
} from '@wix/wix-data-client-common'
import type {
  Editor3DynamicPagesBiParams,
  AppApi,
} from '@wix/wix-data-client-common'
import type { PresetDefinition } from '../../presets/types'
import type {
  EditorSDK,
  FedopsLogger,
  I18n,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import { dynamicPageCustomRouterCreatePageClicked } from '@wix/bi-logger-wix-code-data/v2'
import type { PageAddResult } from '../add-cms-pages/addCmsPages'

type Params = {
  editorSdkProxy: EditorSDK
  appApi: AppApi
  preset: PresetDefinition
  collectionsApi: CollectionsApi
  existingCollectionId: string
  fedopsLogger: FedopsLogger
  i18n: I18n
  logger: Logger
  biParams: Editor3DynamicPagesBiParams
}

export const addPageAndReplaceCollection = async ({
  editorSdkProxy,
  appApi,
  preset,
  collectionsApi,
  existingCollectionId,
  pageLinkFieldKey,
}: Omit<Params, 'fedopsLogger' | 'i18n' | 'logger' | 'biParams'> & {
  pageLinkFieldKey?: string
}) => {
  const newCollectionSchema = await collectionsApi.get(existingCollectionId)

  const { pageRef } = await addPage({
    editorSdkProxy,
    preset,
    collectionName: newCollectionSchema.displayName,
  })

  const { controllerRef } = await findController({
    editorSdkProxy,
    pageRef,
  })

  const { fieldMap } = await getFieldMap({
    preset,
    appApi,
    newCollectionSchema,
    controllerRef,
    pageLinkFieldKey,
  })

  await appApi.replaceDatasetCollection({
    controllerRef,
    newCollectionId: newCollectionSchema.id,
    fieldMap,
  })

  return { pageRef, schema: newCollectionSchema }
}

const interactionName = 'addPageWithRepeater'

export const addPageWithRepeater = async ({
  editorSdkProxy,
  appApi,
  preset,
  collectionsApi,
  existingCollectionId,
  fedopsLogger,
  i18n,
  logger,
  biParams,
}: Params) => {
  fedopsLogger.interactionStarted(interactionName)

  const progressBar = getProgressBar({
    editorSdkProxy,
    logger,
    totalSteps: 1,
    title: i18n.t('Editor3_Add_Pages_Progress_Bar_Title_Creating_Page'),
  })
  await progressBar.open()

  let result: PageAddResult

  try {
    const newCollectionSchema = await collectionsApi.get(existingCollectionId)

    await progressBar.update({
      stepTitle: i18n.t(
        'Editor3_Add_Pages_Progress_Bar_Step_Title_Adding_List_Page',
        {
          collection_name: truncateCollectionName(
            newCollectionSchema.displayName,
          ),
        },
      ),
    })

    result = await runTransactionWithRetry(editorSdkProxy, async () => {
      const result = await addPageAndReplaceCollection({
        editorSdkProxy,
        appApi,
        preset,
        collectionsApi,
        existingCollectionId,
      })

      return result
    })

    await progressBar.update()
  } finally {
    await progressBar.close()
  }
  const { pageRef, schema } = result
  logger.bi(
    dynamicPageCustomRouterCreatePageClicked({
      page_type: 'page_with_repeater',
      collection_name: schema.displayName,
      collection_id: existingCollectionId,
      is_auto_bound: true,
      new_collection: false,
      pageId: pageRef.id,
      action_type: 'add_dynamic_page',
      origin: biParams?.origin,
      template_id: preset.id,
    }),
  )

  await editorSdkProxy.history.add({
    label: UNDO_REDO_LABELS.ADD_PAGE_WITH_REPEATER,
  })

  fedopsLogger.interactionEnded(interactionName)

  await editorSdkProxy.pages.navigateTo({ pageRef })

  return { pageRef }
}
